/** @format */

import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { AiOutlineMessage, AiOutlinePlus } from "react-icons/ai";
import { FaList, FaTimes, FaUserFriends } from "react-icons/fa";
import { GrEmoji } from "react-icons/gr";
import { IoMdSend } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import {
  add_friend,
  clearUnreadForSeller,
  incrementUnread,
  markAllSeen,
  markOutgoingAsSeen,
  send_message,
  updateMessage,
} from "../../store/reducers/chatReducer";
import MessageUx from "../../utils/MessageUx";
import { WhatsAppButton } from "../../utils/utils";

// ─── Socket singleton ───────────────────────────────────────────────────────
const socket = io(process.env.REACT_APP_SERVER_URL, {
  autoConnect: true,
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
});

const Chats = () => {
  const dispatch = useDispatch();
  const { sellerId } = useParams();
  const { customerInfo } = useSelector((state) => state.auth);
  const {
    myFriends,
    fd_messages,
    currentFriend,
    //fd_messages_success,
    unreadPerSeller,
    activeSeller,
    totalCustommerUnread,
  } = useSelector((state) => state.chat);

  const [text, setText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [show, setShow] = useState(false);

  const messagesEndRef = useRef(null);
  const firstUnreadRef = useRef(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  // ── 1. Enregistrer le customer sur le socket ────────────────────────────
  useEffect(() => {
    if (customerInfo?.id) {
      socket.emit("add_customer", customerInfo.id, customerInfo);
    }
  }, [customerInfo]);

  // ── 2. Charger les amis + conversation ──────────────────────────────────
  useEffect(() => {
    dispatch(
      add_friend({ sellerId: sellerId || "", customerId: customerInfo.id }),
    );
  }, [sellerId, customerInfo, dispatch]);

  // ── 3. Marquer comme "seen" dès qu'on ouvre la conversation ─────────────
  //    + à chaque fois que de nouveaux messages arrivent avec status unseen

  useEffect(() => {
    if (!sellerId || !customerInfo?.id || !fd_messages?.length) return;

    const hasUnseen = fd_messages.some(
      (m) => m.senderId === sellerId && m.status === "unseen",
    );

    if (hasUnseen) {
      // Notifier le serveur → qui notifie le seller
      socket.emit("mark_as_seen", {
        sellerId,
        customerId: customerInfo.id,
      });

      // Mise à jour locale immédiate (optimistic update)
      dispatch({
        type: "chat/markAllSeen",
        payload: { sellerId },
      });
    }
  }, [fd_messages, sellerId, customerInfo, dispatch]);

  // ── 4. Réception message entrant du seller ──────────────────────────────
  /*
  useEffect(() => {
    const receiveHandler = (message) => {
      if (
        message.senderId === sellerId &&
        message.receiverId === customerInfo?.id
      ) {
        dispatch(updateMessage(message));

        // Marquer immédiatement comme vu si on est dans cette conv
        socket.emit("mark_as_seen", {
          sellerId: message.senderId,
          customerId: customerInfo.id,
        });
        dispatch({
          type: "chat/markAllSeen",
          payload: { sellerId: message.senderId },
        });
      } else {
        toast.success(
          `${message.senderName || "Vendeur"} vous a envoyé un message`,
        );
      }
    };

    socket.on("receive_seller_message", receiveHandler);
    return () => socket.off("receive_seller_message", receiveHandler);
  }, [sellerId, customerInfo, dispatch]);
*/
  // ── 5. Réception mise à jour "seen" par le seller ───────────────────────
  useEffect(() => {
    const seenHandler = ({ customerId }) => {
      if (customerId === customerInfo?.id) {
        dispatch({ type: "chat/markOutgoingAsSeen", payload: { sellerId } });
      }
    };

    socket.on("messages_seen", seenHandler);
    return () => socket.off("messages_seen", seenHandler);
  }, [customerInfo, sellerId, dispatch]);

  // ── 6. Statuts actifs seller/customer ───────────────────────────────────
  useEffect(() => {
    const activeSellerHandler = (sellers) => {
      dispatch({
        type: "chat/updateSeller",
        payload: Array.isArray(sellers) ? sellers : [],
      });
    };
    const activeCustomerHandler = (customers) => {
      dispatch({
        type: "chat/updateCustomer",
        payload: Array.isArray(customers) ? customers : [],
      });
    };

    socket.on("activeSeller", activeSellerHandler);
    socket.on("activeCustomer", activeCustomerHandler);
    return () => {
      socket.off("activeSeller", activeSellerHandler);
      socket.off("activeCustomer", activeCustomerHandler);
    };
  }, [dispatch]);
  /*
  // ── 7. Envoi message ────────────────────────────────────────────────────
  const handleSendMessage = useCallback(async () => {
    if (!text.trim() && selectedFiles.length === 0) return;

    const formData = new FormData();
    formData.append("customerId", customerInfo.id);
    formData.append("sellerId", sellerId);
    formData.append("senderName", customerInfo.name);
    formData.append("message", text);
    formData.append("messageType", selectedFiles.length > 0 ? "image" : "text");
    selectedFiles.forEach((file) => formData.append("files", file));

    dispatch(send_message(formData));
    setText("");
    setSelectedFiles([]);
    setPreviewUrls([]);
  }, [text, selectedFiles, customerInfo, sellerId, dispatch]);
*/

  const handleSendMessage = useCallback(async () => {
    if (!text.trim() && selectedFiles.length === 0) return;

    const formData = new FormData();
    formData.append("customerId", customerInfo.id);
    formData.append("sellerId", sellerId);
    formData.append("senderName", customerInfo.name);
    formData.append("message", text);
    formData.append("messageType", selectedFiles.length > 0 ? "image" : "text");

    selectedFiles.forEach((file) => formData.append("files", file));

    // ✅ ID UNIQUE (important)
    const clientId = Date.now() + "_" + Math.random();

    const payload = {
      clientId,
      senderId: customerInfo.id,
      receiverId: sellerId,
      message: text,
      messageType: selectedFiles.length > 0 ? "image" : "text",
      createdAt: new Date(),
    };

    // ✅ UNIQUEMENT socket
    socket.emit("send_message_to_seller", payload);

    // ✅ backend async
    dispatch(send_message(formData));

    setText("");
    setSelectedFiles([]);
    setPreviewUrls([]);
  }, [text, selectedFiles, customerInfo, sellerId, dispatch]);
  //____l'ecoute des no lus_______________________________________

  useEffect(() => {
    const handler = ({ customerId }) => {
      dispatch(incrementUnread({ sellerId: customerId }));
    };

    socket.on("new_unread_from_customer", handler);
    return () => socket.off("new_unread_from_customer", handler);
  }, [dispatch]);

  // ── 8. Émettre sur socket après succès Redux ────────────────────────────
  /*
  useEffect(() => {
    if (fd_messages_success && fd_messages?.length) {
      socket.emit(
        "send_message_to_seller",
        fd_messages[fd_messages.length - 1],
      );
      dispatch(messageClear());
    }
  }, [fd_messages_success, fd_messages, dispatch]);
*/
  // ── 9. Scroll auto ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!fd_messages?.length) return;

    const firstUnreadIndex = fd_messages.findIndex(
      (m) => m.receiverId === customerInfo.id && m.status === "unseen",
    );

    const timer = setTimeout(() => {
      if (firstUnreadIndex !== -1 && firstUnreadRef.current) {
        firstUnreadRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }, 80);

    return () => clearTimeout(timer);
  }, [fd_messages, customerInfo.id]);

  // ── 10. Gestion fichiers ────────────────────────────────────────────────
  const handleFileSelect = useCallback((files) => {
    const fileArray = Array.from(files);
    setSelectedFiles(fileArray);
    setPreviewUrls(fileArray.map((file) => URL.createObjectURL(file)));
  }, []);

  useEffect(() => {
    const handlePaste = (e) => {
      for (let item of e.clipboardData.items) {
        if (item.type.includes("image")) handleFileSelect([item.getAsFile()]);
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [handleFileSelect]);

  useEffect(() => {
    const dropZone = dropRef.current;
    if (!dropZone) return;
    const handleDrop = (e) => {
      e.preventDefault();
      handleFileSelect(e.dataTransfer.files);
    };
    const handleDragOver = (e) => e.preventDefault();
    dropZone.addEventListener("drop", handleDrop);
    dropZone.addEventListener("dragover", handleDragOver);
    return () => {
      dropZone.removeEventListener("drop", handleDrop);
      dropZone.removeEventListener("dragover", handleDragOver);
    };
  }, [handleFileSelect]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const lastOutgoingIndex = fd_messages
    ? [...fd_messages]
        .map((m, i) => ({ m, i }))
        .filter(({ m }) => currentFriend?.fdId === m.receiverId)
        .pop()?.i
    : null;

  const isSellerOnline = (fdId) =>
    activeSeller.some(
      (s) => s.sellerId === fdId || s.sellerId?.sellerId === fdId,
    );

  // ── Dans le composant ──────────────────────────────────────

  // 1. Quand le seller lit les messages du customer
  useEffect(() => {
    const handler = ({ sellerId }) => {
      // Marquer dans le store les messages envoyés à ce seller comme "seen"
      dispatch(markOutgoingAsSeen({ sellerId }));
    };

    socket.on("messages_seen_by_seller", handler);
    return () => socket.off("messages_seen_by_seller", handler);
  }, [dispatch]);

  // 2. Quand le customer reçoit un message du seller
  /*
  useEffect(() => {
    const handler = (message) => {
      dispatch((dispatch, getState) => {
        const { fd_messages } = getState().chat;

        // ✅ anti-doublon (id OU clientId)
        const exists = fd_messages.some(
          (m) =>
            m._id === message._id ||
            (message.clientId && m.clientId === message.clientId),
        );

        if (exists) return;

        dispatch(updateMessage(message));
      });

      if (message.senderId === currentFriend?.fdId) {
        socket.emit("seller_read_messages", {
          sellerId: message.senderId,
          customerId: customerInfo.id,
        });

        dispatch(markAllSeen({ sellerId: message.senderId }));
      } else {
        dispatch(incrementUnread({ sellerId: message.senderId }));
        toast(`Nouveau message de ${message.senderName}`);
      }
    };

    socket.on("receive_seller_message", handler);
    return () => socket.off("receive_seller_message", handler);
  }, [currentFriend, dispatch, customerInfo.id]);
*/
  // ── 2. Quand le customer reçoit un message du seller ─────────────────────
  useEffect(() => {
    const handler = (message) => {
      const isCurrentConv = message.senderId === currentFriend?.fdId;

      if (isCurrentConv) {
        // ✅ Seulement si c'est la conv ouverte → insérer dans fd_messages
        dispatch((dispatch, getState) => {
          const { fd_messages } = getState().chat;

          const exists = fd_messages.some(
            (m) =>
              m._id === message._id ||
              (message.clientId && m.clientId === message.clientId),
          );

          if (exists) return;

          dispatch(updateMessage(message));
        });

        // Notifier le seller que le message est lu
        socket.emit("seller_read_messages", {
          sellerId: message.senderId,
          customerId: customerInfo.id,
        });

        dispatch(markAllSeen({ sellerId: message.senderId }));
      } else {
        // ✅ Autre conv → NE PAS toucher fd_messages, juste compteur + toast
        dispatch(incrementUnread({ sellerId: message.senderId }));
        toast(`Nouveau message de ${message.senderName}`);
      }
    };

    socket.on("receive_seller_message", handler);
    return () => socket.off("receive_seller_message", handler);
  }, [currentFriend, dispatch, customerInfo.id]);
  // 3. Quand la conv est ouverte → marquer les messages comme vus INSTANTANEMENT
  useEffect(() => {
    if (!currentFriend?.fdId || !customerInfo?.id) return;

    // Notifier le seller que le customer a ouvert la conv
    socket.emit("seller_read_messages", {
      sellerId: currentFriend.fdId,
      customerId: customerInfo.id,
    });
    //console.log("currentFriend.fdId", currentFriend.fdId);
    // Mettre a jour le store local immédiatement (sans attendre HTTP)
    dispatch(markAllSeen({ sellerId: currentFriend.fdId }));
    dispatch(clearUnreadForSeller(currentFriend.fdId));
  }, [dispatch, customerInfo.id, currentFriend?.fdId]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className='bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden'>
      <div className='flex w-full h-[560px] relative'>
        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <div
          className={`w-[240px] flex-shrink-0 border-r border-gray-100 flex flex-col
            md-lg:absolute md-lg:h-full md-lg:z-40 md-lg:bg-white md-lg:shadow-xl
            transition-all duration-300 ${show ? "md-lg:left-0" : "md-lg:-left-[260px]"}`}
        >
          <div className='flex items-center gap-3 px-4 py-4 border-b border-gray-100'>
            <AiOutlineMessage className='text-blue-500 text-xl' />
            <span className='font-semibold text-gray-800'>Discussions</span>
            {totalCustommerUnread > 0 && (
              <span className='ml-auto text-xs font-bold bg-blue-500 text-white px-2 py-0.5 rounded-full'>
                {totalCustommerUnread}
              </span>
            )}
          </div>

          <div className='flex-1 overflow-y-auto py-2 px-2 space-y-1'>
            {myFriends.map((friend, index) => (
              <Link
                to={`/dashboard/my_chats/${friend?.fdId}`}
                key={index}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-150
                  hover:bg-blue-50 ${sellerId === friend.fdId ? "bg-blue-50 ring-1 ring-blue-200" : ""}`}
              >
                <div className='relative flex-shrink-0'>
                  <img
                    className='w-9 h-9 rounded-full object-cover ring-2 ring-white'
                    src={friend.fdImage}
                    alt={friend.fdName}
                  />
                  {isSellerOnline(friend.fdId) && (
                    <span className='absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-white' />
                  )}
                </div>
                <span className='text-sm font-medium text-gray-700 truncate flex-1'>
                  {friend.fdName}
                </span>
                {unreadPerSeller?.find((c) => c._id === friend?.fdId)?.count >
                  0 && (
                  <span className='text-xs font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full'>
                    {unreadPerSeller.find((c) => c._id === friend?.fdId)?.count}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* ── Zone de chat principale ──────────────────────────────────── */}
        <div className='flex-1 flex flex-col min-w-0'>
          {currentFriend ? (
            <>
              {/* Header */}
              <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white'>
                <div className='flex items-center gap-3'>
                  <div className='relative'>
                    <img
                      className='w-10 h-10 rounded-full object-cover'
                      src={currentFriend.fdImage}
                      alt={currentFriend.fdName}
                    />
                    {isSellerOnline(currentFriend.fdId) && (
                      <span className='absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-white' />
                    )}
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-gray-900'>
                      {currentFriend.fdName || currentFriend?.shopName}
                    </p>
                    <p
                      className={`text-xs font-medium ${isSellerOnline(currentFriend.fdId) ? "text-green-500" : "text-gray-400"}`}
                    >
                      {isSellerOnline(currentFriend.fdId)
                        ? "En ligne"
                        : "Hors ligne"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShow(!show)}
                  className='hidden md-lg:flex w-9 h-9 items-center justify-center rounded-xl bg-blue-500 text-white transition-colors hover:bg-blue-600'
                >
                  {show ? <FaTimes size={14} /> : <FaList size={14} />}
                </button>
              </div>

              {/* Messages */}
              <div
                ref={dropRef}
                className='flex-1 overflow-y-auto px-4 py-4 space-y-1 bg-gray-50'
              >
                {fd_messages?.map((message, index) => {
                  const isOutgoing = currentFriend?.fdId === message.receiverId;
                  const isLastOutgoing = index === lastOutgoingIndex;
                  const isFirstUnread =
                    !isOutgoing &&
                    message.status === "unseen" &&
                    fd_messages.findIndex(
                      (m) =>
                        m.receiverId === customerInfo.id &&
                        m.status === "unseen",
                    ) === index;

                  return (
                    <div key={message._id || index}>
                      {/* Séparateur "nouveaux messages" */}
                      {isFirstUnread && (
                        <div
                          ref={firstUnreadRef}
                          className='flex items-center gap-2 my-3'
                        >
                          <div className='flex-1 h-px bg-blue-200' />
                          <span className='text-xs text-blue-500 font-medium whitespace-nowrap px-2'>
                            Nouveaux messages
                          </span>
                          <div className='flex-1 h-px bg-blue-200' />
                        </div>
                      )}

                      <div
                        className={`flex items-end gap-2 ${isOutgoing ? "justify-end" : "justify-start"}`}
                      >
                        {/* Avatar seller (messages entrants) */}
                        {!isOutgoing && (
                          <img
                            className='w-7 h-7 rounded-full object-cover flex-shrink-0 mb-1'
                            src={currentFriend.fdImage || "/images/seller.png"}
                            alt=''
                          />
                        )}

                        <div className='max-w-[70%] flex flex-col'>
                          {/* ↓ on passe isOutgoing à MessageUx */}
                          <MessageUx
                            message={message}
                            isOutgoing={isOutgoing}
                          />
                        </div>

                        {/* Avatar customer (messages sortants) */}
                        {isOutgoing && (
                          <img
                            className='w-7 h-7 rounded-full object-cover flex-shrink-0 mb-1'
                            src='/images/customer.png'
                            alt=''
                          />
                        )}
                      </div>

                      {/* Bouton WhatsApp sous dernier message sortant */}
                      {isLastOutgoing && (
                        <div className='flex justify-end mt-1.5 pr-9'>
                          <WhatsAppButton
                            sellerPhone={currentFriend?.phone}
                            message={message}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Aperçu images */}
              {previewUrls.length > 0 && (
                <div className='flex gap-2 px-4 py-2 flex-wrap bg-white border-t border-gray-100'>
                  {previewUrls.map((url, i) => (
                    <div key={i} className='relative group'>
                      <img
                        alt='Aperçu'
                        src={url}
                        className='w-16 h-16 object-cover rounded-lg border border-gray-200'
                      />
                      <button
                        onClick={() => {
                          const newFiles = [...selectedFiles];
                          newFiles.splice(i, 1);
                          setSelectedFiles(newFiles);
                          setPreviewUrls(
                            newFiles.map((f) => URL.createObjectURL(f)),
                          );
                        }}
                        className='absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center shadow'
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Barre de saisie */}
              <div className='flex items-center gap-2 px-4 py-3 bg-white border-t border-gray-100'>
                <input
                  multiple
                  type='file'
                  ref={inputRef}
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className='hidden'
                  id='chatFile'
                />
                <label
                  htmlFor='chatFile'
                  className='w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors flex-shrink-0'
                >
                  <AiOutlinePlus className='text-gray-600 text-lg' />
                </label>

                <div className='flex-1 relative'>
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                    type='text'
                    placeholder='Tapez votre message…'
                    className='w-full h-10 pl-4 pr-10 text-sm bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-blue-200 transition-all'
                  />
                  <span className='absolute right-3 top-2.5 text-gray-400 cursor-default'>
                    <GrEmoji size={16} />
                  </span>
                </div>

                <button
                  onClick={handleSendMessage}
                  disabled={!text.trim() && selectedFiles.length === 0}
                  className='w-10 h-10 flex items-center justify-center rounded-xl bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-all flex-shrink-0 active:scale-95'
                >
                  <IoMdSend size={18} />
                </button>
              </div>
            </>
          ) : (
            <div
              onClick={() => setShow(true)}
              className='flex-1 flex flex-col gap-3 justify-center items-center text-gray-400 cursor-pointer select-none'
            >
              <div className='w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center'>
                <FaUserFriends className='text-3xl text-blue-400' />
              </div>
              <p className='text-sm font-medium'>
                Sélectionnez un vendeur pour démarrer
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chats;
